import { ReactNode } from "react";

import { Translate } from "@/app/application/Translate";
import { Select, useTheme } from "@mui/material";
import type { SelectProps } from "@mui/material/Select";
import { useField } from "formik";

interface Props extends React.DetailedHTMLProps<
	React.SelectHTMLAttributes<HTMLSelectElement>,
	HTMLSelectElement
> {
	label: string;
	name: string;
	id: string;
	children: ReactNode;
	placeholder?: string;
	translationString?: string;
	style?: React.CSSProperties;
}

const CustomSelect = ({
	label,
	id,
	translationString,
	children,
	...props
}: Props) => {
	const theme = useTheme();
	const [field, meta] = useField(props);

	const hasError = Boolean(meta.touched && meta.error);

	// Build Select props; cast needed because Props extend HTML select attributes
	const selectProps = {
		...field,
		id,
		...props,
		error: hasError,
		native: true as const,
		sx: {
			height: "40px",
			width: "100%",
			minWidth: 0,
			borderRadius: 2,
			borderColor: hasError ? "error.main" : "inherit",
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: hasError ? "error.main" : "inherit",
			},
		},
	} as unknown as SelectProps<string>;

	return (
		<div className="mui-select">
			<label
				htmlFor={id}
				style={{
					display: "block",
					marginBottom: "8px",
					color: theme.palette.text.secondary,
				}}
			>
				<Translate String={translationString || ""}>{label}</Translate>
			</label>
			<Select {...selectProps}>{children}</Select>
			{hasError && (
				<div
					style={{
						color: theme.palette.error.main,
						fontSize: "0.875rem",
						marginTop: "4px",
					}}
				>
					{meta.error}
				</div>
			)}
		</div>
	);
};

export default CustomSelect;
